import { z } from "zod";
// ─────────────────────────────────────────────────────────────────────────────
// Piste V10 — Comptes Professionnels, PHASE 2 (docs/PRO_ACCOUNTS_SPEC.md)
// Liste d'attente (US-46/47), CRM light (EPIC 15), fidélité (US-156),
// broadcasts (EPIC 7, US-126), consentements (US-146), métriques (US-157).
//
// RÈGLE US-125 (cloison données perso) : aucun DTO exposé à un pro ne contient
// l'email ni le téléphone d'un client — uniquement userId, displayName, handle.
// ─────────────────────────────────────────────────────────────────────────────
// ── Liste d'attente (US-46/47) ───────────────────────────────────────────────
/**
 * Inscription en liste d'attente sur une fenêtre (US-46) :
 * "je veux un créneau entre mardi et jeudi". Le business est dérivé de la
 * route côté serveur (jamais du body).
 */
export const WaitlistEntryInputSchema = z
    .object({
    serviceId: z.string().uuid(),
    windowStart: z.string().datetime(),
    windowEnd: z.string().datetime(),
})
    .refine((w) => new Date(w.windowEnd).getTime() > new Date(w.windowStart).getTime(), {
    message: "windowEnd must be after windowStart",
    path: ["windowEnd"],
})
    // US-46 : fenêtre bornée à 14 jours (cohérente avec le sélecteur de créneaux).
    .refine((w) => new Date(w.windowEnd).getTime() - new Date(w.windowStart).getTime() <=
    14 * 86_400_000, { message: "window must not exceed 14 days", path: ["windowEnd"] });
/**
 * Entrée de liste d'attente. `notifiedAt` + `offeredSlotStart` + `offerExpiresAt`
 * décrivent l'offre à durée limitée en cours (US-46) — null sinon.
 */
export const WaitlistEntryDTOSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    serviceId: z.string().uuid(),
    clientId: z.string().uuid(),
    windowStart: z.string().datetime(),
    windowEnd: z.string().datetime(),
    createdAt: z.string().datetime(),
    notifiedAt: z.string().datetime().nullable(),
    offerExpiresAt: z.string().datetime().nullable(),
    offeredSlotStart: z.string().datetime().nullable(),
});
// ── Contre-proposition (US-54) & annulation pro (EPIC 5) ────────────────────
/** US-54 — le pro propose un autre créneau en réponse à une demande. */
export const ProposeRescheduleInputSchema = z.object({
    newStartAt: z.string().datetime(),
});
/** US-54 — réponse du client à la contre-proposition. */
export const RespondProposalInputSchema = z.object({
    accept: z.boolean(),
});
/** EPIC 5 — annulation d'un RDV par le pro : motif obligatoire. */
export const CancelBookingProInputSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
// ── CRM light : notes privées (US-151) ───────────────────────────────────────
/** Note privée du pro sur un client — jamais visible du client. */
export const ClientNoteInputSchema = z.object({
    body: z.string().trim().min(1).max(2000),
});
export const ClientNoteDTOSchema = z.object({
    businessId: z.string().uuid(),
    clientId: z.string().uuid(),
    body: z.string(),
    updatedAt: z.string().datetime(),
});
// ── Fidélité (US-156) ────────────────────────────────────────────────────────
/** Programme façon carte à tampons : "10 passages = 10e coupe offerte". */
export const LoyaltyProgramInputSchema = z.object({
    target: z.number().int().min(2).max(100),
    reward: z.string().trim().min(1).max(200),
    /**
     * E7 (phase 3) — réduction appliquée au PRÉPAIEMENT quand la carte est
     * pleine : pourcentage 1-100, ou null/absent = gratuité (100 %). Inerte
     * tant que PREPAY_ENABLED=false.
     */
    rewardPercent: z.number().int().min(1).max(100).nullable().optional(),
    active: z.boolean().default(true),
});
export const LoyaltyProgramDTOSchema = z.object({
    businessId: z.string().uuid(),
    target: z.number().int().positive(),
    reward: z.string(),
    /** E7 — réduction au prépaiement (null = gratuité). */
    rewardPercent: z.number().int().min(1).max(100).nullable(),
    active: z.boolean(),
});
/** La carte du client chez ce pro — +1 tampon à chaque RDV COMPLETED. */
export const LoyaltyCardDTOSchema = z.object({
    clientId: z.string().uuid(),
    businessId: z.string().uuid(),
    stamps: z.number().int().nonnegative(),
    redeemed: z.number().int().nonnegative(),
});
// ── Broadcasts : offres & messages à la base (US-73/152/153) ─────────────────
/**
 * Création d'un broadcast. `kind` : "offer" = offre à la base fans opt-in
 * (US-73), "message" = message à la base CRM (US-152) — mêmes quotas et
 * modération (US-126).
 */
export const BroadcastInputSchema = z.object({
    body: z.string().trim().min(10).max(500),
    kind: z.enum(["offer", "message"]).default("offer"),
});
/** `sentAt` null = brouillon. Stats simples : vues et résas générées (US-153). */
export const BroadcastDTOSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    body: z.string(),
    kind: z.string(),
    sentAt: z.string().datetime().nullable(),
    audience: z.number().int().nonnegative(),
    views: z.number().int().nonnegative(),
    bookingsGenerated: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
});
// ── Consentement de partage (US-146) ─────────────────────────────────────────
/**
 * Le 3e consentement, distinct de l'opt-in offres (Follow.optInOffers) :
 * partage des données enrichies du client avec CE pro (périmètre de
 * l'export US-146). Demandé p. ex. à la première résa.
 */
export const ConsentInputSchema = z.object({
    optInShareData: z.boolean(),
});
// ── Fiche client CRM (US-151) ────────────────────────────────────────────────
/**
 * Fiche client vue par le pro (US-151) : identité minimale + historique de
 * RDV CHEZ LUI uniquement. US-125 : JAMAIS d'email ni de téléphone.
 */
export const ClientCRMDTOSchema = z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
    handle: z.string().nullable(),
    /** Stats RDV chez ce pro uniquement. */
    totalBookings: z.number().int().nonnegative(),
    completedBookings: z.number().int().nonnegative(),
    noShows: z.number().int().nonnegative(),
    lastVisitAt: z.string().datetime().nullable(),
    /** Carte de fidélité chez ce pro (null si pas de programme / pas de carte). */
    loyalty: LoyaltyCardDTOSchema.nullable(),
    /** Note privée du pro (US-151) — null si aucune. */
    note: z.string().nullable(),
});
// ── Métriques (US-157, volet gratuit) ────────────────────────────────────────
/** Tableau de bord gratuit : volumes, remplissage, no-shows, audience. */
export const ProMetricsDTOSchema = z.object({
    /** RDV confirmés/terminés de la semaine en cours. */
    rdvWeek: z.number().int().nonnegative(),
    /** RDV confirmés/terminés du mois en cours. */
    rdvMonth: z.number().int().nonnegative(),
    /** Taux de remplissage des créneaux ouverts, 0-1. */
    fillRate: z.number().min(0).max(1),
    /** Part de no-shows parmi les RDV passés, 0-1. */
    noShowRate: z.number().min(0).max(1),
    /** Nouveaux clients vs récurrents sur la période. */
    newVsReturning: z.object({
        new: z.number().int().nonnegative(),
        returning: z.number().int().nonnegative(),
    }),
    /** Nombre d'abonnés (US-72) — compteur uniquement, jamais d'identités (US-125). */
    followers: z.number().int().nonnegative(),
});
//# sourceMappingURL=pro2.js.map